/**
 * Standalone package detail page with shareable URL.
 */

import { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Spin, message, Button, Space, Modal } from 'antd';
import { ArrowLeftOutlined, HistoryOutlined } from '@ant-design/icons';
import { getPackage, updatePackage, updatePackageUploadStatus, failPackageFileUpload, deleteReleaseNote, deleteTestReport, getPreviousGitSources } from '@/api/packages';
import { useUploadProgressStore } from '@/store/uploadProgressStore';
import { getPackageLogs } from '@/api/logs';
import { PackageDetail } from '@/components/packages/PackageDetail';
import { PackageForm, PackageFormData } from '@/components/packages/PackageForm';
import { OperationLog } from '@/components/logs/OperationLog';
import { useConfigStore } from '@/store/configStore';
import { reportPackageUploadRecord } from '@/utils/uploadRecordUtils';
import { getStuckThresholdMs } from '@/utils/uploadThreshold';
import type { BSPPackageDetail, OperationLog as OperationLogType, PreviousGitSource } from '@/types';

const PackageDetailPage = () => {
  const { packageId } = useParams<{ packageId: string }>();
  const navigate = useNavigate();
  const { getProgress } = useUploadProgressStore();
  const { fileTypes, projects, fetchFileTypes, fetchProjects } = useConfigStore();

  const [packageData, setPackageData] = useState<BSPPackageDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLogVisible, setIsLogVisible] = useState(false);
  const [logs, setLogs] = useState<OperationLogType[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [previousGitSources, setPreviousGitSources] = useState<PreviousGitSource[]>([]);
  const uploadStartedAtRef = useRef<string | null>(null);

  // 根据项目配置合并供应商和 Momenta 文件类型
  const currentProjectId = packageData?.project?.id;
  const currentProject = projects.find(p => p.id === currentProjectId) ?? null;
  const availableFileTypes = useMemo(() => {
    const proj = projects.find(p => p.id === currentProjectId);
    if (!proj) return fileTypes;
    const supplierTypes = proj.allowed_file_types || [];
    const momentaTypes = proj.momenta_file_types || [];
    const existingIds = new Set(supplierTypes.map(ft => ft.id));
    const combined = [...supplierTypes];
    for (const ft of momentaTypes) {
      if (!existingIds.has(ft.id)) combined.push(ft);
    }
    return combined.length > 0 ? combined : fileTypes;
  }, [currentProjectId, projects, fileTypes]);

  useEffect(() => {
    fetchFileTypes();
    fetchProjects();
  }, [fetchFileTypes, fetchProjects]);

  useEffect(() => {
    const loadPackage = async () => {
      if (!packageId) return;

      try {
        setLoading(true);
        const data = await getPackage(Number(packageId));
        setPackageData(data);
        document.title = `${data.project.name} - ${data.name} - BSP Package Delivery`;

        // 检测卡住的文件：数据库为 uploading/queued，但本 Tab store 无记录且超过阈值没有心跳
        // 判死口径与后端 upload_watchdog 一致，见 @/utils/uploadThreshold
        const now = Date.now();
        const orphans = (data.files || []).filter(f => {
          if (f.upload_status !== 'uploading' && f.upload_status !== 'queued') return false;
          const entry = getProgress(`package-${Number(packageId)}-file-${f.id}`);
          if (entry && entry.status !== 'failed') return false;
          const updatedAt = f.updated_at ? new Date(f.updated_at).getTime() : Date.now();
          return (now - updatedAt) > getStuckThresholdMs({
            status: f.upload_status,
            pendingObjectKey: f.pending_object_key,
          });
        });

        // 检测卡住的 ReleaseNote/TestReport：name 有值但 url 为空，且 store 无活跃记录
        const packageUpdatedAt = data.updated_at ? new Date(data.updated_at).getTime() : Date.now();
        // If any file is still actively uploading/queued and recently heartbeated, RN/TR are
        // legitimately waiting in queue — don't mark them as stuck.
        const hasActiveFile = (data.files || []).some(f => {
          if (f.upload_status !== 'uploading' && f.upload_status !== 'queued') return false;
          const updatedAt = f.updated_at ? new Date(f.updated_at).getTime() : Date.now();
          return (now - updatedAt) <= getStuckThresholdMs({
            status: f.upload_status,
            pendingObjectKey: f.pending_object_key,
          });
        });
        const PACKAGE_STUCK_THRESHOLD_MS = 5 * 60 * 1000;
        const trProgressPrefix = `package-${Number(packageId)}-doc-test_report`;
        const hasTrProgress = Object.entries(useUploadProgressStore.getState().progress).some(
          ([key, entry]) => key === trProgressPrefix || (key.startsWith(`${trProgressPrefix}-`) && !!entry),
        );
        const rnStuck = !!data.release_note_name && !data.release_note_url
          && !getProgress(`package-${Number(packageId)}-doc-release_note`)
          && (now - packageUpdatedAt) > PACKAGE_STUCK_THRESHOLD_MS
          && !hasActiveFile;
        const trStuck = !!data.test_report_name && !data.test_report_url
          && !hasTrProgress
          && (now - packageUpdatedAt) > PACKAGE_STUCK_THRESHOLD_MS
          && !hasActiveFile;

        const cleanupTasks: Promise<unknown>[] = [
          ...orphans.map(f => failPackageFileUpload(Number(packageId), f.id)),
          ...(rnStuck ? [deleteReleaseNote(Number(packageId))] : []),
          ...(trStuck ? [deleteTestReport(Number(packageId))] : []),
        ];

        if (cleanupTasks.length > 0) {
          await Promise.allSettled(cleanupTasks);
          const fresh = await getPackage(Number(packageId));
          setPackageData(fresh);
        }
      } catch (error) {
        message.error('加载底软包详情失败');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    loadPackage();
    return () => {
      document.title = 'BSP Package Delivery - 底软包交付管理系统';
    };
  }, [packageId]);

  const handleEdit = async () => {
    if (!packageId) return;
    try {
      const fresh = await getPackage(Number(packageId));
      setPackageData(fresh);

      const projectId = fresh.project?.id;
      if (projectId) {
        try {
          const sources = await getPreviousGitSources(projectId, Number(packageId));
          setPreviousGitSources(sources);
        } catch {
          setPreviousGitSources([]);
        }
      }

      uploadStartedAtRef.current = new Date().toISOString();
      setIsEditMode(true);
    } catch {
      message.error('获取包详情失败');
    }
  };

  const handleViewLogs = async () => {
    if (!packageId) return;

    setIsLogVisible(true);
    setLogsLoading(true);
    try {
      const response = await getPackageLogs(Number(packageId));
      setLogs(response.results);
    } catch {
      message.error('获取操作日志失败');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleFormSubmit = async (data: PackageFormData) => {
    if (!packageId) return {} as any;

    setFormLoading(true);

    const snapshotBefore = packageData;

    try {
      const result = await updatePackage(Number(packageId), data);
      setIsEditMode(false);

      const recordResult = snapshotBefore?.project?.id
        ? await reportPackageUploadRecord(result, data, snapshotBefore, snapshotBefore.project.id, fileTypes, uploadStartedAtRef.current)
        : null;
      const uploadRecordId = recordResult?.recordId ?? null;

      // Reload package data
      const updatedData = await getPackage(Number(packageId));
      setPackageData(updatedData);
      return {
        ...result,
        uploadRecordId: uploadRecordId ?? null,
        categorizedFiles: recordResult
          ? { deleted: recordResult.deletedItems, existing: recordResult.existingItems }
          : undefined,
      };
    } catch (error) {
      const err = error as { response?: { status: number; data?: { detail?: string } } };
      if (err.response?.status === 409) {
        Modal.confirm({
          title: '数据冲突',
          content: '该记录已被其他用户修改。是否强制覆盖？',
          okText: '强制覆盖',
          cancelText: '放弃修改',
          onOk: async () => {
            try {
              const retryResult = await updatePackage(Number(packageId), { ...data, force: true });
              message.success('更新成功');
              setIsEditMode(false);
              if (snapshotBefore?.project?.id) {
                await reportPackageUploadRecord(retryResult, data, snapshotBefore, snapshotBefore.project.id, fileTypes);
              }
              const updatedData = await getPackage(Number(packageId));
              setPackageData(updatedData);
            } catch {
              message.error('更新失败');
            }
          },
        });
      } else {
        message.error(err.response?.data?.detail || '更新失败');
      }
      throw error;
    } finally {
      setFormLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleRefresh = async () => {
    if (!packageId) return;
    try {
      const data = await getPackage(Number(packageId));
      setPackageData(data);
      message.success('刷新成功');
    } catch {
      message.error('刷新失败');
    }
  };

  // Auto-poll while package upload is not yet done; reconcile file-level → package-level status
  useEffect(() => {
    if (!packageData || packageData.upload_status === 'done' || packageData.upload_status === 'failed') return;
    if (isEditMode) return; // editing: pause polling to avoid overwriting in-flight user changes
    const id = setInterval(async () => {
      if (!packageId) return;
      try {
        const data = await getPackage(Number(packageId));
        setPackageData(data);

        // Aggregate file-level statuses to derive correct package-level status
        const files = data.files || [];
        const releaseNoteUploading = !!data.release_note_name && !data.release_note_url;
        const testReportUploading = !!data.test_report_name && !data.test_report_url;
        let aggregated: 'uploading' | 'failed' | 'done';
        if (
          files.some(f => f.upload_status === 'uploading' || f.upload_status === 'queued') ||
          releaseNoteUploading ||
          testReportUploading
        ) {
          aggregated = 'uploading';
        } else if (files.some(f => f.upload_status === 'failed')) {
          aggregated = 'failed';
        } else {
          aggregated = 'done';
        }

        if (aggregated !== data.upload_status) {
          await updatePackageUploadStatus(
            Number(packageId), aggregated, `aggregated_from_file_statuses:${aggregated}`
          ).catch(() => {});
          const fresh = await getPackage(Number(packageId));
          setPackageData(fresh);
        }
      } catch {
        // silent
      }
    }, 5000);
    return () => clearInterval(id);
  }, [packageData?.upload_status, packageId, isEditMode]);

  if (loading) {
    return <Spin size="large" description="加载中..." fullscreen />;
  }

  if (!packageData) {
    return (
      <Card>
        <div className="text-center text-gray-500">底软包不存在</div>
      </Card>
    );
  }

  return (
    <div className="p-4">
      <Card
        title={
          <Space>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
            >
              返回
            </Button>
            <span>{packageData.name} - 详情</span>
          </Space>
        }
      >
        {isEditMode ? (
          <PackageForm
            initialData={packageData}
            projects={projects}
            projectId={packageData.project?.id}
            fileTypes={availableFileTypes}
            onSubmit={handleFormSubmit}
            onCancel={() => setIsEditMode(false)}
            loading={formLoading}
            isEdit={true}
            previousGitSources={previousGitSources}
          />
        ) : (
          <PackageDetail
            package={packageData}
            allowedFileTypes={currentProject?.allowed_file_types}
            onEdit={handleEdit}
            onViewLogs={handleViewLogs}
            onClose={handleBack}
            onRefresh={handleRefresh}
          />
        )}
      </Card>

      {/* Operation Log Modal */}
      <Modal
        title={
          <Space>
            <HistoryOutlined />
            操作日志 - {packageData.name}
          </Space>
        }
        open={isLogVisible}
        onCancel={() => setIsLogVisible(false)}
        footer={null}
        width={900}
        destroyOnHidden
      >
        <OperationLog logs={logs} loading={logsLoading} />
      </Modal>
    </div>
  );
};

export default PackageDetailPage;
